///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, Signal, signal, WritableSignal } from '@angular/core';

import { EMPTY, finalize, merge, Observable, of, skipWhile, Subject, switchMap, tap } from 'rxjs';
import { ProjectDragAndDropService } from './project-drag-and-drop.service';
import { ProjectService } from '../services/project/project.service';

import { GtthLocalStorageService } from '../shared/auth/localStorage.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PARAM_CUSTOMER_ID, PARAM_FILTER, PARAM_TREE_ID } from '../shared/constants/url-params.constants';

import { FoldersService } from '../services/project/folders.service';

import { FoldersTreeService, FolderTreeNode, FolderTreeNodeInfo } from './folders-tree/folders-tree.service';
import { ProjectDTO, ProjectFolderDTO, ProjectFolderEnum } from '../generated/shared-types.model';
import { GtthEventManager } from '../services/event-manager.service';
import { takeUntil } from 'rxjs/operators';
import { ProjectsViewStateService } from './projects-view-state.service';
import { PreloaderService } from '../services/preloader.service';
import { queryParamToNumber } from '../shared/utils';
import { PROJECTS_ENG_BASE_URL } from './projects.route';

@Component({
    selector: 'winde-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthProjectsComponent implements OnInit, OnDestroy {
    @Input() baseUrl!: string;

    public readonly projects: WritableSignal<ProjectDTO[]>;
    public readonly activePath: Signal<FolderTreeNodeInfo[]>;
    public readonly selectedFolder: Signal<FolderTreeNode>;

    public projectSortParam = 'name';
    public projectSortDescending = false;

    public companyId?: number;
    private folderId?: number;
    private folders: ProjectFolderDTO[];
    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly eventManager: GtthEventManager,
        private readonly localStorageService: GtthLocalStorageService,
        private readonly projectService: ProjectService,
        private readonly folderService: FoldersService,
        private readonly foldersTreeService: FoldersTreeService,
        private readonly projectsViewStateService: ProjectsViewStateService,
        public readonly projectDragAndDropService: ProjectDragAndDropService,
        public readonly preloaderService: PreloaderService
    ) {
        this.projects = signal([]);
        this.activePath = this.foldersTreeService.getActivePath();
        this.selectedFolder = this.foldersTreeService.getSelectedFolder();
    }

    ngOnInit() {
        this.initSubscriptions();
        this.activatedRoute.queryParamMap
            .pipe(
                skipWhile((params: ParamMap) => {
                    const companyId = queryParamToNumber(params.get(PARAM_CUSTOMER_ID));
                    return this.baseUrl === PROJECTS_ENG_BASE_URL && !companyId;
                }),
                switchMap((params: ParamMap) => {
                    const companyId = queryParamToNumber(params.get(PARAM_CUSTOMER_ID));
                    const folderId = queryParamToNumber(params.get(PARAM_TREE_ID));
                    const isCompanyIdChanged = this.companyId !== companyId;

                    this.projectsViewStateService.setCustomerFilter(params.get(PARAM_FILTER));
                    this.companyId = companyId;
                    this.folderId = folderId;

                    if (!this.companyId && isCompanyIdChanged) {
                        return EMPTY;
                    }

                    return this.loadData(!this.folders || isCompanyIdChanged);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    navigateToParentFolder() {
        this.navigateByFolderId(this.selectedFolder().parentId);
    }

    navigateByFolderId(folderId: number) {
        this.navigate(folderId);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.foldersTreeService.clearTree();
    }

    private initSubscriptions() {
        const projectModified$ = this.eventManager.getEventSubscription('projectModified');
        const folderModified$ = this.eventManager.getEventSubscription('folderModified');
        const innerFilterChanged$ = this.projectsViewStateService.innerFilterChanges$;
        const folderSelectionChanged$ = this.foldersTreeService.selectionChanges$;

        merge(
            projectModified$.pipe(switchMap(() => this.loadData(false))),
            folderModified$.pipe(switchMap(() => this.loadData(true))),
            innerFilterChanged$.pipe(switchMap(() => this.loadData(false))),
            folderSelectionChanged$.pipe(
                skipWhile(() => this.folderId === this.selectedFolder().id),
                tap(() => this.navigate(this.selectedFolder().id))
            )
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    private loadData(shouldReloadFolders: boolean): Observable<ProjectDTO[]> {
        let folders$: Observable<ProjectFolderDTO[]> = of(this.folders);
        if (shouldReloadFolders) {
            this.foldersTreeService.clearTree();
            folders$ = this.folderService.getFolders(this.companyId).pipe(
                tap((folders: ProjectFolderDTO[]): void => {
                    this.folders = folders;
                    this.foldersTreeService.createTree(folders);
                })
            );
        }

        this.preloaderService.show();
        return folders$.pipe(
            skipWhile(() => this.isFolderIdUndefined()),
            skipWhile(() => this.isFolderIdUnrelatedToSelectedCompany()),
            switchMap(() => this.loadProjects()),
            tap((): void => {
                this.foldersTreeService.selectFolder(this.folderId);
                this.localStorageService.setSelectedFolderId(this.folderId);
            }),
            finalize(() => this.preloaderService.hide())
        );
    }

    private isFolderIdUndefined(): boolean {
        if (!this.folderId) {
            const storedFolderId = this.localStorageService.getSelectedFolderId();
            if (storedFolderId && this.isFolderRelatedToSelectedCompany(storedFolderId)) {
                this.navigate(storedFolderId);
            } else {
                this.navigateToProjectFolder();
            }
            return true;
        }
        return false;
    }

    private isFolderIdUnrelatedToSelectedCompany(): boolean {
        if (!this.isFolderRelatedToSelectedCompany(this.folderId)) {
            this.navigateToProjectFolder();
            return true;
        }
        return false;
    }

    private isFolderRelatedToSelectedCompany(folderId: number): boolean {
        return this.folders.some((folder: ProjectFolderDTO): boolean => folder.id === folderId);
    }

    private navigateToProjectFolder(): void {
        const projectFolder: ProjectFolderDTO = this.folders.find(
            (folder: ProjectFolderDTO) => !folder.parentId && folder.type === ProjectFolderEnum.PROJECT
        );
        if (projectFolder) {
            this.navigate(projectFolder.id);
        }
    }

    private loadProjects(): Observable<ProjectDTO[]> {
        this.projects.set([]);
        const searchFilter = this.projectsViewStateService.searchFilter();
        return this.projectService.getProjects(this.folderId, searchFilter).pipe(
            tap((projects: ProjectDTO[]): void => {
                this.projects.set(projects);
            })
        );
    }

    private navigate(folderId: number) {
        this.router.navigate([], {
            queryParams: { [PARAM_TREE_ID]: folderId },
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge'
        });
    }
}
